import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'react-use';

import { GUIDTypeInfo } from '@ndvr/client-gateway-api';

import { API_ROOT } from 'env';

export interface UseSearchStreamResult {
    results: GUIDTypeInfo[];
    isLoading: boolean;
    error: Error | null;
    isComplete: boolean;
    retry: () => void;
}

interface UseSearchStreamOptions {
    enabled?: boolean;
    debounceMs?: number;
    keepPreviousResults?: boolean;
}

export const useSearchStream = (searchTerm: string, options?: UseSearchStreamOptions): UseSearchStreamResult => {
    const { enabled = true, debounceMs = 180, keepPreviousResults = false } = options || {};

    const [results, setResults] = useState<GUIDTypeInfo[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [isComplete, setIsComplete] = useState(false);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
    const [retryKey, setRetryKey] = useState(0);

    const eventSourceRef = useRef<EventSource | null>(null);
    const previousResultsRef = useRef<GUIDTypeInfo[]>([]);

    useDebounce(
        () => {
            setDebouncedSearchTerm(searchTerm);
        },
        debounceMs,
        [searchTerm],
    );

    const closeEventSource = useCallback(() => {
        if (!eventSourceRef.current) {
            return;
        }

        eventSourceRef.current.close();
        eventSourceRef.current = null;
    }, []);

    const retry = useCallback(() => {
        setRetryKey((currentValue) => currentValue + 1);
    }, []);

    useEffect(() => {
        previousResultsRef.current = results;
    }, [results]);

    useEffect(() => {
        setError(null);
        setIsComplete(false);

        if (!enabled || !debouncedSearchTerm) {
            closeEventSource();
            setResults([]);
            setIsLoading(false);
            return;
        }

        const previousResults = previousResultsRef.current;
        const hadPreviousResults = previousResults.length > 0;
        const shouldKeepPreviousResults = keepPreviousResults && hadPreviousResults;
        if (!shouldKeepPreviousResults) {
            setResults([]);
        }

        setIsLoading(true);

        let isActiveConnection = true;
        let hasReceivedResultForCurrentQuery = false;
        let hasSwappedToCurrentResults = !shouldKeepPreviousResults;

        closeEventSource();

        const eventSource = new EventSource(
            `${API_ROOT}/client-gateway/tool/search/stream?searchTerm=${encodeURIComponent(debouncedSearchTerm)}`,
        );
        eventSourceRef.current = eventSource;

        const resultHandler = (event: MessageEvent) => {
            if (!isActiveConnection) {
                return;
            }

            try {
                const result: GUIDTypeInfo = JSON.parse(event.data);
                hasReceivedResultForCurrentQuery = true;

                setResults((currentResults) => {
                    if (!hasSwappedToCurrentResults) {
                        hasSwappedToCurrentResults = true;
                        return [result];
                    }

                    return [...currentResults, result];
                });
            } catch {
                setResults([]);
                setIsLoading(false);
                setError(new Error('Failed to parse search results'));
                setIsComplete(true);
                closeEventSource();
            }
        };

        const resolverErrorHandler = () => {};

        const completeHandler = () => {
            if (!isActiveConnection) {
                return;
            }

            if (shouldKeepPreviousResults && !hasReceivedResultForCurrentQuery) {
                setResults([]);
            }

            setIsLoading(false);
            setIsComplete(true);
            closeEventSource();
        };

        const errorEventHandler = (event: MessageEvent) => {
            if (!isActiveConnection) {
                return;
            }

            setResults([]);
            setIsLoading(false);
            setIsComplete(true);
            setError(new Error(event.data || 'Search failed'));
            closeEventSource();
        };

        eventSource.addEventListener('RESULT', resultHandler);
        eventSource.addEventListener('RESOLVER_ERROR', resolverErrorHandler);
        eventSource.addEventListener('COMPLETE', completeHandler);
        eventSource.addEventListener('ERROR', errorEventHandler);

        eventSource.onerror = () => {
            eventSource.removeEventListener('RESULT', resultHandler);
            eventSource.removeEventListener('RESOLVER_ERROR', resolverErrorHandler);
            eventSource.removeEventListener('COMPLETE', completeHandler);
            eventSource.removeEventListener('ERROR', errorEventHandler);

            closeEventSource();

            if (!isActiveConnection) {
                return;
            }

            setResults([]);
            setIsLoading(false);
            setError(new Error('Failed to connect to search stream'));
            setIsComplete(true);
        };

        return () => {
            isActiveConnection = false;
            eventSource.removeEventListener('RESULT', resultHandler);
            eventSource.removeEventListener('RESOLVER_ERROR', resolverErrorHandler);
            eventSource.removeEventListener('COMPLETE', completeHandler);
            eventSource.removeEventListener('ERROR', errorEventHandler);
            closeEventSource();
        };
    }, [closeEventSource, debouncedSearchTerm, enabled, keepPreviousResults, retryKey]);

    return { results, isLoading, error, isComplete, retry };
};
